import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import BrushRoundedIcon from '@mui/icons-material/BrushRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import MicTranscribeButton from '../components/MicTranscribeButton';
import ConversationStorageControls from '../components/ConversationStorageControls';
import { FASTAPI_HOST } from '../settings/config';
import { DEFAULT_BUDDY } from '../settings/buddyProfiles';
import { getBuddyProfiles } from '../settings/buddySessionCache';
import { normalizeBuddyVoiceId } from '../settings/buddyVoiceCatalog';
import {
  BuddyWorkspace,
  ConversationScaffoldAids,
  ConversationTimeline,
  EndConversationControl,
  WorkspaceComposerArea,
  WorkspaceHeader,
  WorkspaceOutputArea,
} from '../components/buddy-workspace';
import { IMAGE_UPLOAD_ACCEPT, prepareImageForUpload } from '../utils/prepareImageForUpload';

const COPY = {
  'Simplified Chinese': {
    title: '和 Leafy 一起画画',
    subtitle: '把你想到的画面说给我听。你的每一个想法，都可以变成画。',
    startError: '无法开始绘画对话，请稍后再试。',
    inputPlaceholder: '说说你想到了什么……',
    send: '发送',
    end: '结束对话',
    draw: '画出来',
    drawing: '正在把你的想法画出来……',
    result: '这是你的想法变成的画',
    changePicture: '继续说，修改这幅画',
    newPicture: '创作另一幅',
    style: '画面风格',
    enableVoice: '开启伙伴语音',
    disableVoice: '关闭伙伴语音',
    voiceError: 'AI 语音暂时无法播放，请稍后再试。',
    hint: '提示',
    optionsOn: '支持开启',
    optionsOff: '支持关闭',
    leafyVoice: 'Leafy 声音',
    settings: 'Draw It 设置',
    creationSettings: '创作设置',
    speed: '语速',
    preview: '试听',
    previewSample: '你好！我是 Leafy。请把你想到的画面告诉我吧。',
    save: '保存',
    saving: '正在保存……',
    settingsError: '无法保存 Draw It 设置。',
    talkWithLeafy: '和 Leafy 一起画画',
    uploadPhoto: '上传参考照片（可选）',
    changePhoto: '更换参考照片',
    uploadingPhoto: '正在上传……',
    referencePhoto: '参考照片',
    feedbackTitle: 'Leafy 的观察',
    summaryTitle: '你已经描述了：',
    praiseTitle: '做得很棒：',
    suggestionTitle: '下次还可以说说：',
    photoError: '无法上传这张照片。',
    enlargePhoto: '点击放大照片',
    collapsePhoto: '点击恢复为缩略图',
  },
  'Traditional Chinese': {
    title: '和 Leafy 一起畫畫',
    subtitle: '把你想到的畫面說給我聽。你的每一個想法，都可以變成畫。',
    startError: '無法開始繪畫對話，請稍後再試。',
    inputPlaceholder: '說說你想到了什麼……',
    send: '傳送',
    end: '結束對話',
    draw: '畫出來',
    drawing: '正在把你的想法畫出來……',
    result: '這是你的想法變成的畫',
    changePicture: '繼續說，修改這幅畫',
    newPicture: '創作另一幅',
    style: '畫面風格',
    enableVoice: '開啟夥伴語音',
    disableVoice: '關閉夥伴語音',
    voiceError: 'AI 語音暫時無法播放，請稍後再試。',
    hint: '提示',
    optionsOn: '支援開啟',
    optionsOff: '支援關閉',
    leafyVoice: 'Leafy 聲音',
    settings: 'Draw It 設定',
    creationSettings: '創作設定',
    speed: '語速',
    preview: '試聽',
    previewSample: '你好！我是 Leafy。請把你想到的畫面告訴我吧。',
    save: '儲存',
    saving: '正在儲存……',
    settingsError: '無法儲存 Draw It 設定。',
    talkWithLeafy: '和 Leafy 一起畫畫',
    uploadPhoto: '上傳參考照片（可選）',
    changePhoto: '更換參考照片',
    uploadingPhoto: '正在上傳……',
    referencePhoto: '參考照片',
    feedbackTitle: 'Leafy 的觀察',
    summaryTitle: '你已經描述了：',
    praiseTitle: '做得很棒：',
    suggestionTitle: '下次還可以說說：',
    photoError: '無法上傳這張照片。',
    enlargePhoto: '點擊放大照片',
    collapsePhoto: '點擊恢復為縮圖',
  },
  English: {
    title: 'Draw with Leafy',
    subtitle: 'Tell me what you can imagine. Every idea you share can become part of your picture.',
    startError: 'The drawing conversation could not start. Please try again.',
    inputPlaceholder: 'Tell me what you imagine…',
    send: 'Send',
    end: 'End conversation',
    draw: 'Draw it',
    drawing: 'Turning your ideas into a picture…',
    result: 'Here is your idea as a picture',
    changePicture: 'Keep talking to change it',
    newPicture: 'Make another',
    style: 'Picture style',
    enableVoice: 'Turn on Buddy voice',
    disableVoice: 'Turn off Buddy voice',
    voiceError: 'Buddy voice is unavailable right now. Please try again.',
    hint: 'Hint',
    optionsOn: 'Support ON',
    optionsOff: 'Support OFF',
    leafyVoice: 'Leafy voice',
    settings: 'Draw It Settings',
    creationSettings: 'Creation Settings',
    speed: 'Speed',
    preview: 'Preview',
    previewSample: 'Hello! I am Leafy. Tell me what you would like to draw.',
    save: 'Save',
    saving: 'Saving...',
    settingsError: 'Could not save Draw It settings.',
    talkWithLeafy: 'Draw with Leafy',
    uploadPhoto: 'Upload a reference photo (optional)',
    changePhoto: 'Change reference photo',
    uploadingPhoto: 'Uploading...',
    referencePhoto: 'Reference photo',
    feedbackTitle: "Leafy's observations",
    summaryTitle: 'You already described:',
    praiseTitle: 'You did a great job:',
    suggestionTitle: 'Next time, you could also mention:',
    photoError: 'The reference photo could not be uploaded.',
    enlargePhoto: 'Click to enlarge photo',
    collapsePhoto: 'Click to return to thumbnail',
  },
};

const STYLES = [
  { value: 'children_book', translationKey: 'creative_drawing_style_storybook' },
  { value: 'crayon', translationKey: 'creative_drawing_style_crayon' },
  { value: 'watercolor', translationKey: 'creative_drawing_style_watercolor' },
  { value: 'paper_cut', translationKey: 'creative_drawing_style_paper_cut' },
];

function errorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  return (typeof detail === 'string' ? detail : detail?.message)
    || error?.message
    || fallback;
}

const buddyCopy = (copy, name) => Object.fromEntries(
  Object.entries(copy).map(([key, value]) => [key, typeof value === 'string' ? value.replaceAll('Leafy', name) : value]),
);

export default function CreativeDrawingPage({
  currentLang = 'English',
  childName = '',
  childAge = 8,
  childInterest = '',
  roleLanguage = '',
  conversationLevel = 'intermediate',
  paidAccess = false,
  planChecked = false,
  onPurchase,
}) {
  const [buddy, setBuddy] = useState(DEFAULT_BUDDY);
  const text = buddyCopy(COPY[currentLang] || COPY.English, buddy.name);
  const [translations, setTranslations] = useState({});
  const t = useMemo(
    () => translations?.[currentLang] || {},
    [translations, currentLang],
  );
  const startedRef = useRef(false);
  const chatEndRef = useRef(null);
  const photoInputRef = useRef(null);
  const audioRef = useRef(null);
  const audioUrlRef = useRef('');
  const ttsAbortRef = useRef(null);
  const lastSpokenTextRef = useRef('');
  const [sessionId, setSessionId] = useState('');
  const [version, setVersion] = useState(0);
  const [messages, setMessages] = useState([]);
  const [actions, setActions] = useState([]);
  const [currentChoices, setCurrentChoices] = useState([]);
  const [currentHint, setCurrentHint] = useState('');
  const [showScaffoldAids, setShowScaffoldAids] = useState(true);
  const [answer, setAnswer] = useState('');
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [error, setError] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [style, setStyle] = useState('children_book');
  const [voiceBroadcastEnabled, setVoiceBroadcastEnabled] = useState(false);
  const [voicePlaying, setVoicePlaying] = useState(false);
  const [leafyVoice, setLeafyVoice] = useState('ai_01');
  const [leafySpeechSpeed, setLeafySpeechSpeed] = useState(1);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [referencePreviewUrl, setReferencePreviewUrl] = useState('');
  const [referencePhotoExpanded, setReferencePhotoExpanded] = useState(false);
  const [drawingFeedback, setDrawingFeedback] = useState(null);
  const [storageMode, setStorageMode] = useState('temporary');

  const authConfig = () => ({
    headers: { Authorization: `Bearer ${Cookies.get('user_token') || ''}` },
  });

  const applyView = (view) => {
    if (view?.context?.persistence_warning) setError(view.context.persistence_warning);
    setSessionId(view?.session_id || '');
    setStorageMode(view?.context?.storage_mode || 'temporary');
    setVersion(Number(view?.version) || 0);
    setMessages(Array.isArray(view?.messages) ? view.messages : []);
    setActions(Array.isArray(view?.actions) ? view.actions : []);
    setCurrentChoices(
      Array.isArray(view?.input?.choices)
        ? view.input.choices.map((choice) => choice?.label).filter(Boolean)
        : [],
    );
    setCurrentHint(String(view?.input?.hint || ''));
    const restoredReferenceUrl = String(view?.context?.reference_image_url || '');
    if (restoredReferenceUrl) {
      setReferencePreviewUrl((currentUrl) => {
        if (currentUrl?.startsWith('blob:')) URL.revokeObjectURL(currentUrl);
        return restoredReferenceUrl;
      });
    } else if (view?.context?.has_reference_photo === false) {
      setReferencePreviewUrl((currentUrl) => {
        if (currentUrl?.startsWith('blob:')) URL.revokeObjectURL(currentUrl);
        return '';
      });
      setReferencePhotoExpanded(false);
    }
  };

  const latestAssistantText = (viewMessages = messages) => {
    const list = Array.isArray(viewMessages) ? viewMessages : [];
    for (let index = list.length - 1; index >= 0; index -= 1) {
      if (list[index]?.role === 'assistant') {
        return String(list[index]?.content || '').trim();
      }
    }
    return '';
  };

  const stopSpeaking = () => {
    if (ttsAbortRef.current) {
      ttsAbortRef.current.abort();
      ttsAbortRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = '';
    }
    setVoicePlaying(false);
  };

  const speakText = async (spokenText) => {
    const content = String(spokenText || '').trim();
    if (!content) return;

    if (
      lastSpokenTextRef.current === content
      && audioRef.current
    ) {
      stopSpeaking();
      return;
    }

    stopSpeaking();
    setVoicePlaying(true);
    lastSpokenTextRef.current = content;
    const controller = new AbortController();
    ttsAbortRef.current = controller;

    try {
      const response = await axios.post(
        `${FASTAPI_HOST}/api/photo-story/tts`,
        {
          text: content,
          voice: leafyVoice,
          language: currentLang || 'English',
        },
        {
          ...authConfig(),
          responseType: 'arraybuffer',
          signal: controller.signal,
        },
      );
      if (ttsAbortRef.current === controller) ttsAbortRef.current = null;

      const blob = new Blob([response.data], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.playbackRate = Number(leafySpeechSpeed) || 1;
      audio.preservesPitch = true;
      audio.mozPreservesPitch = true;
      audio.webkitPreservesPitch = true;
      audioRef.current = audio;
      audioUrlRef.current = url;

      const releaseAudio = () => {
        if (audioRef.current === audio) audioRef.current = null;
        if (audioUrlRef.current === url) {
          URL.revokeObjectURL(url);
          audioUrlRef.current = '';
        }
        setVoicePlaying(false);
      };
      audio.onended = releaseAudio;
      audio.onerror = () => {
        releaseAudio();
        setError(text.voiceError);
      };
      await audio.play();
    } catch (requestError) {
      if (requestError?.code === 'ERR_CANCELED') return;
      stopSpeaking();
      setError(text.voiceError);
    }
  };

  const toggleVoiceBroadcast = async () => {
    if (voiceBroadcastEnabled) {
      setVoiceBroadcastEnabled(false);
      stopSpeaking();
      return;
    }
    setVoiceBroadcastEnabled(true);
    await speakText(latestAssistantText());
  };

  const startConversation = async (activeBuddy = buddy) => {
    if (starting) return;
    startedRef.current = true;
    stopSpeaking();
    setStarting(true);
    setError('');
    setImageUrl('');
    setDrawingFeedback(null);
    setReferencePhotoExpanded(false);
    setReferencePreviewUrl((currentUrl) => {
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      return '';
    });
    try {
      const normalizedLevel = String(
        conversationLevel || 'intermediate',
      ).toLowerCase();
      const response = await axios.post(
        `${FASTAPI_HOST}/api/conversations/text`,
        {
          definition: 'creative_drawing_interview',
          child_name: childName || '',
          child_age: Number(childAge) || 8,
          child_interest: childInterest || '',
          role_language: roleLanguage || '',
          language: currentLang || 'English',
          conversation_level: (
            ['basic', 'intermediate', 'advanced'].includes(normalizedLevel)
              ? normalizedLevel
              : 'intermediate'
          ),
          buddy_name: activeBuddy.name,
          buddy_personality: activeBuddy.personality,
        },
        authConfig(),
      );
      applyView(response.data);
    } catch (requestError) {
      setError(errorMessage(requestError, text.startError));
    } finally {
      setStarting(false);
    }
  };

  const uploadReferencePhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !sessionId || uploadingPhoto) return;

    setUploadingPhoto(true);
    setError('');
    let previewUrl = '';
    try {
      const preparedFile = await prepareImageForUpload(file);
      previewUrl = URL.createObjectURL(preparedFile);
      const formData = new FormData();
      formData.append('image', preparedFile);
      formData.append('expected_version', String(version));
      const response = await axios.post(
        `${FASTAPI_HOST}/api/conversations/${sessionId}/reference-image`,
        formData,
        {
          ...authConfig(),
          headers: {
            ...authConfig().headers,
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      applyView(response.data);
      setReferencePreviewUrl((currentUrl) => {
        if (currentUrl) URL.revokeObjectURL(currentUrl);
        return previewUrl;
      });
      setDrawingFeedback(null);
      setReferencePhotoExpanded(false);
    } catch (requestError) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setError(errorMessage(requestError, text.photoError));
    } finally {
      setUploadingPhoto(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getBuddyProfiles(),
      axios.get(`${FASTAPI_HOST}/api/user-preferences`, authConfig()),
    ])
      .then(([profiles, preferencesResponse]) => {
        if (cancelled) return;
        const preferredId = window.localStorage.getItem('freeTalkBuddyId');
        const selected = profiles.find((item) => item.id === preferredId) || profiles.find((item) => item.isDefault) || profiles[0] || DEFAULT_BUDDY;
        const selectedWithAvatar = selected;
        setBuddy(selectedWithAvatar);
        if (selectedWithAvatar.voice) setLeafyVoice(normalizeBuddyVoiceId(selectedWithAvatar.voice));
        setLeafySpeechSpeed(Number(preferencesResponse.data?.photo_leafy_speech_speed) || 1);
      })
      .catch(() => {
        // Voice preferences are optional; keep the same defaults as Photo Story.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleBuddyUpdate = (event) => {
      if (event.detail?.id) setBuddy(event.detail);
      if (event.detail?.voice) setLeafyVoice(event.detail.voice);
      if (event.detail?.speed) setLeafySpeechSpeed(Number(event.detail.speed) || 1);
      if (event.detail?.id && startedRef.current) startConversation(event.detail);
    };
    window.addEventListener('buddy-settings-updated', handleBuddyUpdate);
    return () => window.removeEventListener('buddy-settings-updated', handleBuddyUpdate);
  }, []);

  useEffect(() => {
    fetch('/translations.json')
      .then((response) => response.json())
      .then((data) => setTranslations(data))
      .catch(() => setTranslations({}));
  }, []);

  useEffect(() => () => {
    stopSpeaking();
  }, []);

  useEffect(() => () => {
    if (referencePreviewUrl) URL.revokeObjectURL(referencePreviewUrl);
  }, [referencePreviewUrl]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, submitting]);

  const hasAction = (id) => actions.some(
    (action) => action?.id === id && action?.enabled !== false
  );
  const canDraw = hasAction('generate_artifact');

  const sendAction = async (type, value = '') => {
    if (!sessionId || submitting) return;
    stopSpeaking();
    setSubmitting(true);
    setError('');
    try {
      const response = await axios.post(
        `${FASTAPI_HOST}/api/conversations/${sessionId}/actions`,
        {
          request_id: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
          expected_version: version,
          type,
          value,
          conversation_language: currentLang,
        },
        authConfig(),
      );
      applyView(response.data);
      setAnswer('');
      if (voiceBroadcastEnabled) {
        await speakText(latestAssistantText(response.data?.messages));
      }
    } catch (requestError) {
      setError(errorMessage(requestError, 'Could not send your idea.'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitAnswer = () => {
    const value = answer.trim();
    if (value) sendAction('submit_text', value);
  };

  const generatePicture = async () => {
    if (!sessionId || !canDraw || drawing) return;
    stopSpeaking();
    setDrawing(true);
    setError('');
    try {
      const response = await axios.post(
        `${FASTAPI_HOST}/api/conversations/${sessionId}/artifacts/image`,
        { style, size: '1024x1024' },
        { ...authConfig(), timeout: 180000 },
      );
      const encoded = response.data?.image_base64;
      if (!encoded) throw new Error('The image service returned no picture.');
      setReferencePhotoExpanded(false);
      setImageUrl(`data:${response.data?.mime_type || 'image/png'};base64,${encoded}`);
      setDrawingFeedback(response.data?.feedback || null);
    } catch (requestError) {
      setError(errorMessage(requestError, 'The picture could not be created.'));
    } finally {
      setDrawing(false);
    }
  };

  const displayedMessages = useMemo(
    () => messages.filter((message) => message?.role !== 'system'),
    [messages],
  );

  return (
    <BuddyWorkspace activity="creative_drawing" maxWidth={920} sx={{ pt: { xs: 9, sm: 9 }, pb: { xs: 2, md: 4 }, px: 2 }}>
      {/* <Stack alignItems="center" spacing={1} sx={{ mb: 3, textAlign: 'center' }}>
        <Box sx={{
          width: 58, height: 58, borderRadius: '50%', display: 'grid',
          placeItems: 'center', color: '#fff',
          background: 'linear-gradient(135deg, #6b9d52, #ec9d49)',
        }}>
          <BrushRoundedIcon fontSize="large" />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#254227' }}>
          {t?.creative_drawing_title || text.title}
        </Typography>
        <Typography sx={{ color: '#607064', maxWidth: 620 }}>{text.subtitle}</Typography>
      </Stack> */}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{
        borderRadius: 4, overflow: 'hidden', border: '1px solid #D6DED2',
        boxShadow: '0 18px 50px rgba(52, 83, 45, 0.12)',
      }}>
        {imageUrl ? (
          <WorkspaceOutputArea>
          <Stack spacing={2.5} sx={{ alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{text.result}</Typography>
            <Stack
              direction={{
                xs: 'column',
                md: referencePreviewUrl && referencePhotoExpanded ? 'row' : 'column',
              }}
              spacing={2}
              alignItems="center"
              justifyContent="center"
              sx={{ width: '100%' }}
            >
              {referencePreviewUrl && referencePhotoExpanded && (
                <Stack spacing={0.75} alignItems="center" sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700 }}>{text.referencePhoto}</Typography>
                  <Tooltip title={text.collapsePhoto}>
                    <ButtonBase
                      onClick={() => setReferencePhotoExpanded(false)}
                      aria-label={text.collapsePhoto}
                      aria-expanded="true"
                      sx={{ borderRadius: 3, overflow: 'hidden', width: '100%' }}
                    >
                      <Box
                        component="img"
                        src={referencePreviewUrl}
                        alt={text.referencePhoto}
                        sx={{
                          width: '100%',
                          maxWidth: 420,
                          maxHeight: 420,
                          objectFit: 'contain',
                          borderRadius: 3,
                          boxShadow: 2,
                        }}
                      />
                    </ButtonBase>
                  </Tooltip>
                </Stack>
              )}
              <Box
                sx={{
                  width: '100%',
                  maxWidth: referencePreviewUrl && referencePhotoExpanded ? 420 : 620,
                  flex: 1,
                  position: 'relative',
                }}
              >
                <Box
                  component="img"
                  src={imageUrl}
                  alt={text.result}
                  sx={{
                    display: 'block',
                    width: '100%',
                    borderRadius: 3,
                    boxShadow: 3,
                  }}
                />
                {referencePreviewUrl && !referencePhotoExpanded && (
                  <Tooltip title={text.enlargePhoto}>
                    <ButtonBase
                      onClick={() => setReferencePhotoExpanded(true)}
                      aria-label={text.enlargePhoto}
                      aria-expanded="false"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        width: 64,
                        height: 64,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '3px solid #fff',
                        boxShadow: 3,
                        bgcolor: '#fff',
                      }}
                    >
                      <Box
                        component="img"
                        src={referencePreviewUrl}
                        alt={text.referencePhoto}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </ButtonBase>
                  </Tooltip>
                )}
              </Box>
            </Stack>
            {drawingFeedback && (
              <Card
                variant="outlined"
                sx={{
                  width: '100%',
                  maxWidth: 700,
                  p: 2,
                  borderRadius: 3,
                  bgcolor: '#fffaf0',
                  borderColor: '#f2c879',
                }}
              >
                <Stack spacing={1}>
                  <Typography sx={{ fontWeight: 800 }}>
                    {text.feedbackTitle}
                  </Typography>
                  <Typography
                    aria-label={`${drawingFeedback.stars || 1} out of 5 stars`}
                    sx={{ color: '#e99a20', fontSize: 28, letterSpacing: 2 }}
                  >
                    {'★'.repeat(Number(drawingFeedback.stars) || 1)}
                    {'☆'.repeat(5 - (Number(drawingFeedback.stars) || 1))}
                  </Typography>
                  {drawingFeedback.summary && (
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>
                        {text.summaryTitle}
                      </Typography>
                      <Typography>{drawingFeedback.summary}</Typography>
                    </Box>
                  )}
                  {drawingFeedback.praise && (
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>
                        {text.praiseTitle}
                      </Typography>
                      <Typography>{drawingFeedback.praise}</Typography>
                    </Box>
                  )}
                  {Array.isArray(drawingFeedback.suggestions)
                    && drawingFeedback.suggestions.length > 0 && (
                    <Box>
                      <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                        {text.suggestionTitle}
                      </Typography>
                      {drawingFeedback.suggestions.map((suggestion, index) => (
                        <Typography key={`${suggestion}-${index}`} sx={{ ml: 1 }}>
                          • {suggestion}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Stack>
              </Card>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="contained"
                startIcon={<BrushRoundedIcon />}
                onClick={() => setImageUrl('')}
                sx={{ bgcolor: '#274D3B', '&:hover': { bgcolor: '#1F3D30' } }}
              >
                {text.changePicture}
              </Button>
              <Button variant="outlined" disabled={starting} startIcon={starting ? <CircularProgress size={18} /> : null} onClick={() => {
                startedRef.current = true;
                startConversation();
              }}>
                {text.newPicture}
              </Button>
            </Stack>
          </Stack>
          </WorkspaceOutputArea>
        ) : (
          <>
            <WorkspaceHeader avatar={buddy.image} buddyId={buddy.id} avatarFallback={buddy.reactions?.neutral || '🙂'} title={text.talkWithLeafy} badge="Beta" actions={<>
              <ConversationStorageControls sessionId={sessionId} storageMode={storageMode} definitions={['creative_drawing_interview']} onRestore={applyView} onStorageModeChange={setStorageMode} currentLang={currentLang} />
                <input
                  ref={photoInputRef}
                  type="file"
                  accept={IMAGE_UPLOAD_ACCEPT}
                  onChange={uploadReferencePhoto}
                  hidden
                />
                <Tooltip title={uploadingPhoto
                  ? text.uploadingPhoto
                  : referencePreviewUrl
                    ? text.changePhoto
                    : text.uploadPhoto}
                >
                  <span>
                    <IconButton
                      onClick={() => photoInputRef.current?.click()}
                      disabled={!sessionId || submitting || drawing || uploadingPhoto}
                      aria-label={referencePreviewUrl ? text.changePhoto : text.uploadPhoto}
                      sx={{ border: '1px solid #d1d5db', color: '#6b7280' }}
                    >
                      {uploadingPhoto
                        ? <CircularProgress size={20} color="inherit" />
                        : <CloudUploadRoundedIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              </>} />
            {!sessionId ? (
              <Box sx={{ minHeight: 330, display: 'grid', placeItems: 'center', px: 2, py: 5, textAlign: 'center', bgcolor: '#FDFCF8' }}>
                <Stack spacing={2} alignItems="center" sx={{ maxWidth: 620 }}>
                  <Typography role="note" sx={{ color: 'text.secondary', maxWidth: 520 }}>
                    {t?.creative_drawing_instruction || text.subtitle}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => (paidAccess ? startConversation(buddy) : onPurchase?.())}
                    aria-label={t?.creative_drawing_start_aria || 'Start drawing conversation'}
                    disabled={!planChecked || starting}
                    startIcon={starting ? <CircularProgress size={18} color="inherit" /> : null}
                    sx={{ bgcolor: '#274D3B', borderRadius: 999, px: 4, py: 1.25, fontWeight: 800, '&:hover': { bgcolor: '#1F3D30' } }}
                  >
                    {starting
                      ? (t?.workspace_starting || 'Starting…')
                      : (t?.workspace_lets_start || 'Let’s start!')}
                  </Button>
                  {!planChecked ? (
                    <Typography variant="caption" color="text.secondary">{t?.workspace_checking_plan || 'Checking your plan…'}</Typography>
                  ) : !paidAccess ? (
                    <Typography variant="caption" color="text.secondary">{t?.workspace_paid_required || 'A paid plan is required to start this activity.'}</Typography>
                  ) : null}
                </Stack>
              </Box>
            ) : (<>
            {referencePreviewUrl && (
              <Box sx={{ px: 2, pt: 1.5, bgcolor: '#FDFCF8' }}>
                <Stack
                  direction={referencePhotoExpanded ? 'column' : 'row'}
                  spacing={1.25}
                  alignItems={referencePhotoExpanded ? 'flex-start' : 'center'}
                >
                  <Tooltip
                    title={
                      referencePhotoExpanded
                        ? text.collapsePhoto
                        : text.enlargePhoto
                    }
                  >
                    <ButtonBase
                      onClick={() => setReferencePhotoExpanded((value) => !value)}
                      aria-label={
                        referencePhotoExpanded
                          ? text.collapsePhoto
                          : text.enlargePhoto
                      }
                      aria-expanded={referencePhotoExpanded}
                      sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid #dfe9da',
                        transition: 'width 180ms ease, height 180ms ease',
                        width: referencePhotoExpanded ? '100%' : 58,
                        maxWidth: referencePhotoExpanded ? 560 : 58,
                      }}
                    >
                      <Box
                        component="img"
                        src={referencePreviewUrl}
                        alt={text.referencePhoto}
                        sx={{
                          display: 'block',
                          width: referencePhotoExpanded ? '100%' : 58,
                          height: referencePhotoExpanded ? 'auto' : 58,
                          maxHeight: referencePhotoExpanded ? '60vh' : 58,
                          objectFit: referencePhotoExpanded ? 'contain' : 'cover',
                          bgcolor: '#fff',
                        }}
                      />
                    </ButtonBase>
                  </Tooltip>
                  <Typography sx={{ fontWeight: 700, color: '#477b3c' }}>
                    {text.referencePhoto} · {
                      referencePhotoExpanded
                        ? text.collapsePhoto
                        : text.enlargePhoto
                    }
                  </Typography>
                </Stack>
              </Box>
            )}
            <ConversationTimeline
              messages={displayedMessages}
              busy={submitting}
              endRef={chatEndRef}
              minHeight={330}
              maxHeight="52vh"
              reactionRenderer={(message) => <Typography component="span" role="img" aria-label={`${buddy.name} reaction: ${message.reaction || 'neutral'}`} sx={{ fontSize: 18, lineHeight: 1 }}>{buddy.reactions?.[message.reaction] || buddy.reactions?.neutral || '🙂'}</Typography>}
            />

            <WorkspaceComposerArea>
              <ConversationScaffoldAids
                choices={currentChoices}
                hint={currentHint}
                visible={showScaffoldAids}
                disabled={submitting || drawing}
                language={currentLang}
                onSelect={(value) => sendAction('submit_text', value)}
                onSpeak={speakText}
                onSkip={(label) => sendAction('skip_question', label)}
                trailingActions={<>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={showScaffoldAids}
                        onChange={(event) => setShowScaffoldAids(event.target.checked)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#3A6752' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#3A6752' },
                        }}
                      />
                    )}
                    label={showScaffoldAids ? text.optionsOn : text.optionsOff}
                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 14, fontWeight: 700 } }}
                  />
                  <Tooltip title={voiceBroadcastEnabled ? text.disableVoice : text.enableVoice}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={toggleVoiceBroadcast}
                        disabled={submitting || drawing || !latestAssistantText()}
                        aria-label={voiceBroadcastEnabled ? text.disableVoice : text.enableVoice}
                        aria-pressed={voiceBroadcastEnabled}
                        sx={{
                          border: '1px solid #d1d5db',
                          color: voiceBroadcastEnabled ? '#2f7d32' : '#6b7280',
                        }}
                      >
                        {voicePlaying
                          ? <CircularProgress size={20} color="inherit" />
                          : voiceBroadcastEnabled
                          ? <VolumeUpRoundedIcon />
                          : <VolumeOffRoundedIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </>}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                <TextField
                  fullWidth
                  autoComplete="off"
                  value={answer}
                  onChange={(event) => setAnswer(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      submitAnswer();
                    }
                  }}
                  placeholder={text.inputPlaceholder}
                  disabled={submitting || drawing}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <MicTranscribeButton
                          insideInput
                          currentLang={currentLang}
                          followUiLanguage
                          disabled={submitting || drawing}
                          audioOutputActive={voicePlaying}
                          onTranscript={(value) => setAnswer(String(value || '').trim())}
                          onError={setError}
                          onRecordingStart={stopSpeaking}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  endIcon={<SendRoundedIcon />}
                  disabled={!answer.trim() || submitting || drawing}
                  onClick={submitAnswer}
                  sx={{ bgcolor: '#274D3B', minWidth: 112, '&:hover': { bgcolor: '#1F3D30' } }}
                >
                  {text.send}
                </Button>
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                spacing={1.5}
                sx={{ mt: 2 }}
              >
                <Stack direction="row" spacing={1}>
                  <EndConversationControl
                    label={text.end}
                    disabled={submitting || drawing}
                    onEnd={(label) => sendAction('end_conversation', label)}
                  />
                </Stack>
                <Stack direction="row" spacing={1}>
                  <FormControl size="small">
                    <Select
                      value={style}
                      onChange={(event) => setStyle(event.target.value)}
                      aria-label={t.creative_drawing_style_label || text.style}
                    >
                      {STYLES.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {t[item.translationKey] || item.value.replaceAll('_', ' ')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    startIcon={drawing ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeRoundedIcon />}
                    disabled={!canDraw || drawing || submitting}
                    onClick={generatePicture}
                    sx={{
                      bgcolor: '#C96F2D',
                      minWidth: 130,
                      '&:hover': { bgcolor: '#A9561F' },
                    }}
                  >
                    {drawing ? text.drawing : text.draw}
                  </Button>
                </Stack>
              </Stack>
            </WorkspaceComposerArea>
            </>)}
          </>
        )}
      </Card>

    </BuddyWorkspace>
  );
}
